import { useTranslation } from 'react-i18next'
import { useBasemapProvider, type BasemapProvider } from '../hooks/useBasemapProvider.js'

// BasemapToggle — small pill toggle rendered as an overlay on top of the
// Mapbox / MapLibre map (typically top-right of the map container). Lets
// the user flip between Mapbox (default, richer UX) and TianDiTu
// (China-compliant 国家测绘地理信息局 basemap). When the build was shipped
// without VITE_TIANDITU_TOKEN the toggle renders nothing — the TianDiTu
// option would not produce a usable style.
//
// Used inside RouteMap + DiscoverMapView; positioned absolutely by the
// parent so the toggle floats over the map canvas without disrupting the
// Mapbox control columns.

export function BasemapToggle() {
  const { t } = useTranslation('common')
  const { provider, setProvider, tiandituAvailable } = useBasemapProvider()
  if (!tiandituAvailable) return null

  const options: Array<{ value: BasemapProvider; label: string }> = [
    { value: 'mapbox', label: t('basemap.mapbox') },
    { value: 'tianditu', label: t('basemap.tianditu') },
  ]

  return (
    <div
      role="group"
      aria-label={t('basemap.providerLabel')}
      data-testid="basemap-toggle"
      className="inline-flex items-center gap-0.5 rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]/95 p-0.5 shadow-[var(--xr-shadow-md)] backdrop-blur"
    >
      {options.map((option) => {
        const active = option.value === provider
        return (
          <button
            key={option.value}
            type="button"
            data-testid={`basemap-toggle-${option.value}`}
            aria-pressed={active}
            onClick={() => setProvider(option.value)}
            className={
              'inline-flex h-6 items-center whitespace-nowrap rounded-full px-2.5 text-[11px] font-medium transition ' +
              (active
                ? 'bg-[var(--xr-brand-primary)] text-[var(--xr-brand-primary-ink)]'
                : 'text-[var(--xr-text-secondary)] hover:text-[var(--xr-text-primary)]')
            }
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
